// lib/convertMdx.js
export function convertMdxToMd(mdx) {
  // Normalise line endings (CRLF/CR -> LF) and drop a leading BOM first: every
  // regex below expects "\n". A checkout with core.autocrlf=true has CRLF .md
  // files, and the front matter then showed as text at the top of each page.
  let md = mdx.replace(/^\uFEFF/, "").replace(/\r\n?/g, "\n");

  // Strip frontmatter, extract title + description as markdown
  md = md.replace(/^---\n([\s\S]*?)\n---\n?/, (_, frontmatter) => {
    const titleMatch = frontmatter.match(/title:\s*['"]?(.+?)['"]?\s*$/m);
    const descMatch = frontmatter.match(/description:\s*['"]?(.+?)['"]?\s*$/m);
    let result = "";
    if (titleMatch) result += `# ${titleMatch[1]}\n\n`;
    if (descMatch) result += `> ${descMatch[1]}\n\n`;
    return result;
  });

  // ── STEP 0: Protect fenced code blocks ────────────────────────────────────
  const codePlaceholders = [];
  md = md.replace(/```[\s\S]*?```/g, (match) => {
    const index = codePlaceholders.length;
    codePlaceholders.push(match);
    return `%%CODE_${index}%%`;
  });

  // ── STEP 0: Convert Cards ─────────────────────────────────────────────────
  md = md.replace(/<CardGroup[^>]*>([\s\S]*?)<\/CardGroup>/g, (_, inner) => {
  const cards = [];
  const cardRegex = /<Card\s([^>]*)>([\s\S]*?)<\/Card>/g;
  let match;
  while ((match = cardRegex.exec(inner)) !== null) {
    const attrs = match[1];
    const body  = match[2].trim();
    const titleMatch = attrs.match(/title="([^"]+)"/);
    const iconMatch  = attrs.match(/icon="([^"]+)"/);
    const title = titleMatch ? encodeURIComponent(titleMatch[1]) : "";
    const icon  = iconMatch  ? iconMatch[1] : "";
    cards.push(`%%CARD|${icon}|${title}|${body}%%`);
  }
  return "\n" + cards.join("\n") + "\n";
});

  // ── STEP 0: Convert Accordion groups ─────────────────────────────────────
  md = md.replace(/<AccordionGroup[^>]*>([\s\S]*?)<\/AccordionGroup>/g, (_, inner) => {
    const accordions = [];
    const accordionRegex = /<Accordion\s([^>]*)>([\s\S]*?)<\/Accordion>/g;
    let match;
    while ((match = accordionRegex.exec(inner)) !== null) {
      const attrs = match[1];
      const body = match[2].trim();
      const titleMatch = attrs.match(/title="([^"]+)"/);
      const iconMatch  = attrs.match(/icon="([^"]+)"/);
      const title = titleMatch ? encodeURIComponent(titleMatch[1]) : "";  // encode
      const icon  = iconMatch  ? iconMatch[1]  : "";
      accordions.push(`%%ACCORDION|${icon}|${title}|${body}%%`);
    }
    return "\n" + accordions.join("\n") + "\n";
  });

  // ── STEP 0: Convert <iframe> tags ─────────────────────────────────────────
  md = md.replace(/<iframe\s([^>]*)>[\s\S]*?<\/iframe>/gi, (_, attrs) => {
    const srcMatch    = attrs.match(/src=["']([^"']+)["']/);
    const widthMatch  = attrs.match(/width=["']([^"']+)["']/);
    const heightMatch = attrs.match(/height=["']([^"']+)["']/);
    if (!srcMatch) return "";
    const src    = srcMatch[1];
    const width  = widthMatch  ? widthMatch[1]  : "100%";
    const height = heightMatch ? heightMatch[1] : "500";
    return `%%IFRAME|${src}|${width}|${height}%%`;
  });

  // ── STEP 0.5: Convert <Icon> BEFORE table protection ──────────────────────
md = md.replace(/<Icon\s([^>]*)\/?>/gi, (_, attrs) => {
  const iconMatch   = attrs.match(/icon=["']([^"']+)["']/);
  const sizeMatch   = attrs.match(/size=["']([^"']+)["']/);
  const variantMatch = attrs.match(/variant=["']([^"']+)["']/);
  if (!iconMatch) return "";
  const icon    = iconMatch[1];
  const size    = sizeMatch    ? sizeMatch[1]    : "16";
  const variant = variantMatch ? variantMatch[1] : "solid";
  const prefix  = variant === "regular" ? "fa-regular" : "fa-solid";
  return `<i class="${prefix} fa-${icon}" style="font-size:${size}px"></i>`;
});

  // ── STEP 1: Protect <table> blocks ─────────────────────────────────────────
  const tablePlaceholders = [];
  md = md.replace(/<table[\s\S]*?<\/table>/gi, (match) => {  // gi flags - case insensitive
    const index = tablePlaceholders.length;
    tablePlaceholders.push(match);
    return `\n\n%%TABLE_${index}%%\n\n`;
  });

  // ── STEP 2: Now safe to convert <img> tags outside tables ──────────────────
  md = md.replace(/<img\s([^>]*)\/?>/gi, (_, attrs) => {
    const srcMatch = attrs.match(/src=["']([^"']+)["']/);
    const altMatch = attrs.match(/alt=["']([^"']+)["']/);
    const widthMatch = attrs.match(/width=["']([^"']+)["']/);
    const heightMatch = attrs.match(/height=["']([^"']+)["']/);
    if (!srcMatch) return "";
    const src = srcMatch[1];
    const alt = altMatch ? altMatch[1] : "";
    const width = widthMatch ? widthMatch[1] : "";
    const height = heightMatch ? heightMatch[1] : "";
    const sizeTag =
      width || height ? `|${width}${height ? ` x ${height}` : ""}` : "";
    return `![${alt}${sizeTag}](${src})\n`;
  });

  // ── STEP 3: Convert CardGroup → markdown list ──────────────────────────────
  md = md.replace(/<CardGroup[^>]*>([\s\S]*?)<\/CardGroup>/g, (_, inner) => {
    const cards = [];
    const cardRegex = /<Card\s([^>]*)>([\s\S]*?)<\/Card>/g;
    let match;
    while ((match = cardRegex.exec(inner)) !== null) {
      const attrs = match[1];
      const body = match[2].trim();
      const titleMatch = attrs.match(/title=["']([^"']+)["']/);
      const hrefMatch = attrs.match(/href=["']([^"']+)["']/);
      const title = titleMatch ? titleMatch[1] : "Link";
      const href = hrefMatch ? hrefMatch[1] : "#";
      cards.push(`- [**${title}**](${href}): ${body}`);
    }
    return "\n" + cards.join("\n") + "\n";
  });

  // ── STEP 3.5: Convert <Icon> tags to Font Awesome <i> HTML ─────────────────
  md = md.replace(/<Icon\s([^>]*)\/?>/gi, (_, attrs) => {
    const iconMatch = attrs.match(/icon=["']([^"']+)["']/);
    const sizeMatch = attrs.match(/size=["']([^"']+)["']/);
    if (!iconMatch) return "";
    const icon = iconMatch[1];
    const size = sizeMatch ? sizeMatch[1] : "16";
    return `<i class="fa-solid fa-${icon}" style="font-size:${size}px"></i>`;
  });

  // ── STEP 4: Strip remaining JSX ────────────────────────────────────────────
  md = md.replace(/<[A-Z][a-zA-Z]*\s[^>]*\/>/g, "");
  md = md.replace(
    /<[A-Z][a-zA-Z]*[^>]*>([\s\S]*?)<\/[A-Z][a-zA-Z]*>/g,
    (_, inner) => inner.trim(),
  );
  md = md.replace(/\{\/\*[\s\S]*?\*\/\}/g, "");
  md = md.replace(/\{[^}]+\}/g, "");

  /// ── STEP 5: Restore table blocks ───────────────────────────────────────────
  tablePlaceholders.forEach((table, index) => {
  md = md.replace(`%%TABLE_${index}%%`, `\n\n${table}\n\n`);
  });

  // ── STEP 6: Restore code blocks ────────────────────────────────────────────
  codePlaceholders.forEach((code, index) => {
    md = md.replace(`%%CODE_${index}%%`, code);
  });

  // Clean up excessive blank lines
  md = md.replace(/\n{3,}/g, "\n\n");

  return md.trim();
}